/**
 * Module #28 chunk 4 — offline photo queue for the cataloging app.
 *
 * Cataloging workers operate in warehouses with spotty wifi. The capture
 * loop (toggle inventory/no → photos → notes → swipe) must NOT block
 * because the upload-photo-to-r2 EF returned a network error. This
 * module persists captured photos to IndexedDB + auto-flushes when the
 * browser/native shell reports online.
 *
 * Design constraints (per `memory/feedback_cataloging_app_zero_friction.md`):
 *   - The worker swipe must complete IMMEDIATELY regardless of upload
 *     status — no spinners, no confirmation dialogs.
 *   - Photos queued while offline must NOT lose context — each photo
 *     records WHO took it (their user id) and goes out only while that
 *     person is signed in, with their token as it is at the moment of the
 *     upload (2026-10-23, move P14). It used to keep the token itself, which
 *     (a) expired within the hour, after which every queued photo was
 *     refused, and (b) left a sign-in credential on a shared device after
 *     its owner signed out. Items queued before that still carry a token;
 *     it is read only to learn whose photo it is, never sent, and dropped
 *     from the stored item at the next flush, before anything is sent.
 *   - Queue survives app kill / device reboot — IDB-backed, not in-memory.
 *
 * Public API:
 *   enqueuePhoto(item)   — store + return immediately
 *   flushQueue()         — try to upload everything; safe to call from
 *                          online listener or manual retry button
 *   subscribeQueueSize() — push notifications for the "X queued" badge
 *
 * The queue is shared across the app — bidder pages don't enqueue
 * (they don't take photos), but if they ever do we want one queue, not
 * per-route ones. Single IDB database named `auctisync-photo-queue`,
 * object store `queue`.
 *
 * Upload mechanics mirror AdminCatalog's `addPhotoToLot` so behavior on
 * the server side is identical — same EF, same FormData shape.
 */

// deno-lint-ignore-file no-explicit-any

import { type AuctisyncSupabase, personOfToken, sendEdgeFunction } from '@auctisync/api-client';
import { supabase as appSupabase } from './supabase';

const DB_NAME = 'auctisync-photo-queue';
const STORE = 'queue';
const DB_VERSION = 1;

export interface QueueItem {
  /** UUID generated at enqueue time. */
  id: string;
  /** The actual photo file (Blob serializable in IDB). */
  file: Blob;
  /** Filename for the upload — preserves user's original or device-generated. */
  filename: string;
  /** Lot the photo is being attached to. */
  lotId: string;
  /** Who took the photo: it is uploaded only while they are signed in. */
  ownerId?: string;
  /**
   * LEGACY (items queued before 2026-10-23): the token at capture. Read only
   * to learn the owner; never sent; dropped at the next flush.
   */
  accessToken?: string;
  /** Captured-at timestamp (ms). */
  capturedAt: number;
  /** Upload-retry count. */
  attempts: number;
}

interface IDBHelpers {
  db: IDBDatabase;
}

let dbPromise: Promise<IDBHelpers> | null = null;

function openDb(): Promise<IDBHelpers> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBHelpers>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve({ db: req.result });
    req.onerror = () => reject(req.error ?? new Error('idb open failed'));
  });
  return dbPromise;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const { db } = await openDb();
  return await new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result: T;
    void Promise.resolve(fn(store))
      .then((r) => {
        result = r;
      })
      .catch(reject);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error ?? new Error('idb tx failed'));
    tx.onabort = () => reject(tx.error ?? new Error('idb tx aborted'));
  });
}

function storePut(store: IDBObjectStore, item: QueueItem): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = store.put(item);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('idb put failed'));
  });
}

function storeGetAll(store: IDBObjectStore): Promise<QueueItem[]> {
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as QueueItem[]);
    req.onerror = () => reject(req.error ?? new Error('idb getAll failed'));
  });
}

function storeDelete(store: IDBObjectStore, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('idb delete failed'));
  });
}

function storeCount(store: IDBObjectStore): Promise<number> {
  return new Promise((resolve, reject) => {
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb count failed'));
  });
}

// ─── Subscribers (queue-size badge / banner) ─────────────────────────────

type SizeSubscriber = (size: number) => void;
const subscribers = new Set<SizeSubscriber>();
let lastBroadcastSize = 0;

async function broadcastSize(): Promise<void> {
  try {
    const size = await withStore('readonly', (s) => storeCount(s));
    lastBroadcastSize = size;
    for (const s of subscribers) {
      try {
        s(size);
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore — queue may be unavailable in non-browser env
  }
}

export function subscribeQueueSize(fn: SizeSubscriber): () => void {
  subscribers.add(fn);
  // Push the last-known size synchronously so the subscriber doesn't
  // wait on broadcastSize() before painting a "0" badge.
  fn(lastBroadcastSize);
  // Also kick a fresh count.
  void broadcastSize();
  return () => {
    subscribers.delete(fn);
  };
}

// ─── Public API ──────────────────────────────────────────────────────────

export interface EnqueueArgs {
  file: File | Blob;
  filename: string;
  lotId: string;
  /** The signed-in person's user id at capture. */
  ownerId: string;
}

/**
 * Persist a photo to the queue. Returns the queue item id. Safe to call
 * from any context — never throws on the happy path.
 */
export async function enqueuePhoto(args: EnqueueArgs): Promise<string> {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `q_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const item: QueueItem = {
    id,
    file: args.file,
    filename: args.filename,
    lotId: args.lotId,
    ownerId: args.ownerId,
    capturedAt: Date.now(),
    attempts: 0,
  };
  await withStore('readwrite', (s) => storePut(s, item));
  void broadcastSize();
  return id;
}

/** The queue's storage, as the flush uses it. */
export interface QueueStore {
  all(): Promise<QueueItem[]>;
  put(item: QueueItem): Promise<void>;
  remove(id: string): Promise<void>;
  count(): Promise<number>;
}

const idbStore: QueueStore = {
  all: () => withStore('readonly', (s) => storeGetAll(s)),
  put: (item) => withStore('readwrite', (s) => storePut(s, item)),
  remove: (id) => withStore('readwrite', (s) => storeDelete(s, id)),
  count: () => queueSize(),
};

/**
 * Currently-queued photo count. Cheap.
 */
export async function queueSize(): Promise<number> {
  try {
    return await withStore('readonly', (s) => storeCount(s));
  } catch {
    return 0;
  }
}

export interface FlushOptions {
  /** The client whose session, retry rule and functions address the uploads use. Default: the app's. */
  supabase?: AuctisyncSupabase;
  /** The fetch to upload with. Default: the global one. */
  fetch?: typeof fetch;
  /** Where the queue is kept. Default: this device's IndexedDB. */
  store?: QueueStore;
  /**
   * Called after EACH successful upload so callers can refresh lot
   * photos / dismiss the queued badge incrementally.
   */
  onItemUploaded?: (item: QueueItem) => void;
  /**
   * Called when an item fails after retries. Caller can show a manual
   * retry UI. The item stays in the queue for next flush attempt.
   */
  onItemFailed?: (item: QueueItem, error: string) => void;
}

/** Whose photo this is: the recorded owner, or (legacy items) the token's person. */
export function ownerOf(item: QueueItem): string | null {
  return item.ownerId ?? personOfToken(item.accessToken);
}

export type QueuedUpload =
  | { kind: 'uploaded' }
  /** Reached the server and was refused (after the shared one-refresh retry). */
  | { kind: 'refused'; status: number; detail: string }
  /** Someone else is signed in (or the owner is unknown): the photo waits for its owner. */
  | { kind: 'waiting_for_owner' }
  /** Nobody is signed in: nothing can be sent. */
  | { kind: 'signed_out' };

/**
 * Upload one queued photo with its owner's CURRENT token and the shared
 * one-refresh, one-retry rule (`@auctisync/api-client`, move P14). Throws
 * only when nothing came back (a network failure).
 */
export async function uploadQueuedItem(
  item: QueueItem,
  options: Pick<FlushOptions, 'supabase' | 'fetch'> = {},
): Promise<QueuedUpload> {
  const owner = ownerOf(item);
  if (owner === null) return { kind: 'waiting_for_owner' };

  const fd = new FormData();
  const fileBlob =
    item.file instanceof File
      ? item.file
      : new File([item.file], item.filename, {
          type: item.file.type || 'image/jpeg',
        });
  fd.append('file', fileBlob);
  fd.append('lot_id', item.lotId);

  const sent = await sendEdgeFunction(
    options.supabase ?? appSupabase,
    'upload-photo-to-r2',
    { method: 'POST', body: fd },
    { onlyFor: owner, ...(options.fetch !== undefined ? { fetch: options.fetch } : {}) },
  );
  if (sent.outcome === 'signed_out') return { kind: 'signed_out' };
  if (sent.outcome === 'someone_else') return { kind: 'waiting_for_owner' };
  if (sent.result.ok) return { kind: 'uploaded' };
  return { kind: 'refused', status: sent.result.status, detail: await sent.result.text() };
}

/** The item as it is stored from now on: owner recorded, no token kept. */
export function withoutToken(item: QueueItem, attempts: number): QueueItem {
  const owner = ownerOf(item);
  const stored: QueueItem = { ...item, ...(owner !== null ? { ownerId: owner } : {}), attempts };
  delete stored.accessToken;
  return stored;
}

/**
 * Attempt to drain the queue. Serial uploads (server-side ratelimit
 * friendliness; the cataloging worker rarely queues >10 photos at a
 * time anyway). Successful items are removed from IDB. Failed items
 * stay; the caller can retry or hand them to the next flush.
 *
 * Returns the count uploaded successfully + the count remaining.
 */
export async function flushQueue(options: FlushOptions = {}): Promise<{
  uploaded: number;
  remaining: number;
}> {
  const store = options.store ?? idbStore;
  let uploaded = 0;

  // A token kept by an item queued before 2026-10-23 is dropped first, before
  // anything is sent: a flush that sends nothing (nobody signed in, the owner
  // away) must not leave it on a shared device either.
  const items: QueueItem[] = [];
  for (const queued of await store.all()) {
    if (queued.accessToken === undefined) {
      items.push(queued);
      continue;
    }
    const kept = withoutToken(queued, queued.attempts);
    await store.put(kept);
    items.push(kept);
  }
  // Sort by capturedAt so older photos go first.
  items.sort((a, b) => a.capturedAt - b.capturedAt);

  for (const item of items) {
    try {
      const result = await uploadQueuedItem(item, options);

      if (result.kind === 'uploaded') {
        await store.remove(item.id);
        uploaded += 1;
        options.onItemUploaded?.(item);
      } else if (result.kind === 'signed_out') {
        // Nothing can go until someone signs in; the rest would say the same.
        break;
      } else if (result.kind === 'waiting_for_owner') {
        // Another person's photo on a shared device: it waits for them. Not a
        // failure, so no attempt is counted.
      } else {
        // Bump attempts so the retry UI knows how stuck it is. Stays in IDB.
        await store.put({ ...item, attempts: item.attempts + 1 });
        options.onItemFailed?.(item, `HTTP ${result.status}: ${result.detail.slice(0, 200)}`);
      }
    } catch (err) {
      // Network error — keep in queue, bump attempts.
      await store.put({ ...item, attempts: item.attempts + 1 });
      options.onItemFailed?.(
        item,
        err instanceof Error ? err.message : 'network error during flush',
      );
      // Abort the rest of the flush — if one POST died on network,
      // the others probably will too. Try again next time.
      break;
    }
  }

  void broadcastSize();
  const remaining = await store.count();
  return { uploaded, remaining };
}

/**
 * Wire automatic flushing to the browser/native online event. Returns
 * a teardown function. Safe to call multiple times — internal guard
 * prevents duplicate listeners.
 *
 * Pattern: caller mounts ONCE at app start (e.g. from the same place
 * useCapacitorDeepLinkAuth lives) so the queue drains regardless of
 * which route the user navigated to when connectivity returns.
 */
let autoFlushInstalled = false;
let autoFlushTeardown: (() => void) | null = null;

export function installAutoFlush(options: FlushOptions = {}): () => void {
  if (autoFlushInstalled && autoFlushTeardown) return autoFlushTeardown;
  autoFlushInstalled = true;

  const handler = () => {
    void flushQueue(options).catch(() => {
      // ignore — onItemFailed already surfaced specifics
    });
  };

  // Web: navigator + window 'online' event.
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('online', handler);
  }

  // Native: @capacitor/network 'networkStatusChange' event with
  // connected=true. Dynamic-import so non-Capacitor envs don't break.
  let nativeRemove: (() => Promise<void>) | null = null;
  void (async () => {
    try {
      const mod = (await import('@capacitor/network')) as {
        Network?: {
          addListener: (
            event: 'networkStatusChange',
            cb: (status: { connected: boolean }) => void,
          ) => Promise<{ remove: () => Promise<void> }>;
        };
      };
      if (mod.Network) {
        const sub = await mod.Network.addListener('networkStatusChange', (status) => {
          if (status.connected) handler();
        });
        nativeRemove = () => sub.remove();
      }
    } catch {
      // not running in Capacitor — silent
    }
  })();

  // Fire once on install in case we're already online + have items.
  if (typeof navigator !== 'undefined' && navigator.onLine !== false) {
    handler();
  }

  autoFlushTeardown = () => {
    if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
      window.removeEventListener('online', handler);
    }
    if (nativeRemove) void nativeRemove();
    autoFlushInstalled = false;
    autoFlushTeardown = null;
  };
  return autoFlushTeardown;
}

/**
 * Test-only reset hook. Production never calls this.
 */
export function _resetQueueForTests(): void {
  dbPromise = null;
  subscribers.clear();
  lastBroadcastSize = 0;
  autoFlushInstalled = false;
  autoFlushTeardown = null;
}
